import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { LeaseStatus } from '@zg-rentals/ts-rental-manager-api-client';
import type { Lease } from '../../models';
import { LeaseAction, Property } from '../../models';
import * as ActionComponents from './Actions/components';
import { LEASE_ACTION_TYPES } from './Actions';
import { LeaseTypes } from '../../models/Types';
import { useLeaseQuery, usePropertyInfoQuery } from '../../utils/queries';
import { useLeasingUrlParams } from '../../hooks';

function getAcceptableActionsByLeaseState(lease: Lease): Array<LEASE_ACTION_TYPES> {
  const baseActionsByState: { [state: string]: Array<LEASE_ACTION_TYPES> } = {
    [LeaseStatus.ACTIVE]: [LEASE_ACTION_TYPES.TERMINATE_LEASE, LEASE_ACTION_TYPES.DEACTIVATE_LEASE],
    [LeaseStatus.COMPLETE_DRAFT]: [LEASE_ACTION_TYPES.REQUEST_SIGNATURES],
    [LeaseStatus.INCOMPLETE_DRAFT]: [LEASE_ACTION_TYPES.REQUEST_SIGNATURES_BLOCKER],
    [LeaseStatus.COUNTERSIGNING]: [LEASE_ACTION_TYPES.COUNTERSIGN_LEASE, LEASE_ACTION_TYPES.CANCEL_SIGNING],
    [LeaseStatus.SENT_COMPLETE_DRAFT]: [LEASE_ACTION_TYPES.REQUEST_SIGNATURES],
    [LeaseStatus.SENT_INCOMPLETE_DRAFT]: [LEASE_ACTION_TYPES.REQUEST_SIGNATURES_BLOCKER],
    [LeaseStatus.SENT_FOR_ONLINE_SIGNING]: [LEASE_ACTION_TYPES.CANCEL_SIGNING],
    [LeaseStatus.SIGNED_BY_TENANT]: [LEASE_ACTION_TYPES.CANCEL_SIGNING],
    [LeaseStatus.SIGNING_CANCELLED]: [LEASE_ACTION_TYPES.REQUEST_SIGNATURES],
    [LeaseStatus.SIGNING_COMPLETE]: [LEASE_ACTION_TYPES.TERMINATE_LEASE, LEASE_ACTION_TYPES.DEACTIVATE_LEASE],
  };

  if (lease.leaseType === LeaseTypes.SIGNED_SELF_UPLOAD) {
    const restrictedStates = [
      LeaseStatus.COMPLETE_DRAFT,
      LeaseStatus.SENT_COMPLETE_DRAFT,
      LeaseStatus.SIGNING_CANCELLED,
    ];

    if (restrictedStates.includes(lease.status)) {
      return [];
    }
  }

  return baseActionsByState[lease.status] || [];
}

export function QueryParamActivatedActions() {
  const history = useHistory();
  const location = useLocation();
  const { leaseId, propertyId } = useLeasingUrlParams();

  const urlParams = new URLSearchParams(location.search);
  const activeActionParam = urlParams.get('activeAction') as LEASE_ACTION_TYPES;

  const leaseQuery = useLeaseQuery(leaseId);
  const propertyQuery = usePropertyInfoQuery(propertyId, !!propertyId);

  const lease = leaseQuery.data;
  const property = propertyQuery.data ? new Property({ ...propertyQuery.data, propertyId }) : undefined;

  const [activeAction, setActiveAction] = useState<LeaseAction | null>(null);

  const removeActiveActionQueryParam = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('activeAction');

    const searchString = searchParams.toString();
    const newUrl = searchString ? `${location.pathname}?${searchString}` : location.pathname;
    history.replace(newUrl);
  }, [history, location]);

  function terminateActiveAction() {
    setActiveAction(null);
    removeActiveActionQueryParam();
  }

  useEffect(() => {
    if (!lease || !activeActionParam) {
      return;
    }

    const acceptableActions = getAcceptableActionsByLeaseState(lease);

    if (acceptableActions.includes(activeActionParam)) {
      const newAction = new LeaseAction(activeActionParam);
      if (activeAction?.name !== newAction.name) {
        setActiveAction(newAction);
      }
    } else {
      removeActiveActionQueryParam();
    }
  }, [lease, activeActionParam, removeActiveActionQueryParam, activeAction]);

  function renderActionComponent() {
    const ActionComponent = ActionComponents[activeAction.getComponent()];

    return (
      ActionComponent && (
        <ActionComponent
          history={history}
          terminateActiveAction={terminateActiveAction}
          lease={lease}
          property={property}
        />
      )
    );
  }

  return activeAction && renderActionComponent();
}

export default QueryParamActivatedActions;
